import React, { useState } from "react";
import { Input } from "../ui/Input";
import { Button, buttonVariants } from "../ui/Button";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../ui/Form";
import { useAppContext } from "../../../context/AppContext";
import { captureConfiguredDoor } from "../../../lib/captureConfiguredDoor";
import useLocalStorage from "../../../lib/UseLocalStorage";
import QuotePriceSummary from "./QuotePriceSummary";
import PriceAccessNote from "./PriceAccessNote";
import { basketEcommerceParams, trackEvent } from "../../../lib/analytics";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faCheck, faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";

const QuoteForm = ({ methods, doorSvgRef }) => {
    const { control } = methods;
    const { csrfToken, state, setView, setCurrentPage } = useAppContext();
    const { setValue } = useLocalStorage();
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [basket, setBasket] = useState(null);
    const [tradeInfoExpanded, setTradeInfoExpanded] = useState(false);
    const { setError: setFormError, clearErrors, setFocus } = methods;

    const key = window.location.pathname.split('/')[1];
    const customerStripeEnabled = Boolean(window.initialData?.customer_stripe_enabled);

    const applyServerErrors = (serverErrors) => {
        if (!serverErrors || typeof serverErrors !== "object") {
            return false;
        }

        clearErrors();

        Object.entries(serverErrors).forEach(([field, messages]) => {
            const message = Array.isArray(messages) ? messages[0] : String(messages);
            setFormError(field, { type: "server", message });
        });

        const firstField = Object.keys(serverErrors)[0];
        if (firstField) {
            setFocus(firstField);
        }

        return true;
    };

    const onSubmit = async (e) => {
        try {
            setError("");
            clearErrors();
            setLoading(true);

            const previousView = state.view;
            const waitForPaint = () =>
                new Promise((resolve) =>
                    requestAnimationFrame(() => requestAnimationFrame(resolve))
                );

            // Get the fixed viewBox from the SVG to ensure consistent framing
            const fixedViewBox = doorSvgRef.current?.getAttribute("viewBox");


            setView("external");
            await waitForPaint();
            const doorImageExternal = await captureConfiguredDoor(doorSvgRef, fixedViewBox);

            setView("internal");
            await waitForPaint();
            const doorImageInternal = await captureConfiguredDoor(doorSvgRef, fixedViewBox);

            setView(previousView);
            await waitForPaint();

            if (customerStripeEnabled) {
                const res = await axios.post("/basket", {
                    ...e,
                    door_image_external: doorImageExternal,
                    door_image_internal: doorImageInternal,
                    _token: csrfToken,
                    key,
                    type: "retail",
                });

                if (res?.data?.basket?.id) {
                    setLoading(false);
                    setBasket(res.data.basket);
                    setCompleted(true);
                    const ecommerce = basketEcommerceParams(res.data.basket, "retail");
                    trackEvent("generate_lead", ecommerce);
                    trackEvent("add_to_cart", ecommerce);
                    setValue("basketId", res?.data?.basket?.id);
                    return;
                }

                if (res?.data?.status === "error") {
                    const applied = applyServerErrors(res?.data?.errors);
                    setError((!applied && res?.data?.message) ? res.data.message : "");
                    setLoading(false);
                    return;
                }

                setError("Something went wrong.");
                setLoading(false);
                return;
            }

            const quoteRes = await axios.post(`/quotes/${key}`, {
                ...e,
                door_image_external: doorImageExternal,
                door_image_internal: doorImageInternal,
                _token: csrfToken,
            });

            if (quoteRes?.data?.status === "success") {
                trackEvent("generate_lead", { customer_type: "retail", lead_source: "quote_request" });
                setLoading(false);
                setCompleted(true);
                return;
            }

            if (quoteRes?.data?.status === "error") {
                const applied = applyServerErrors(quoteRes?.data?.errors);
                setError((!applied && quoteRes?.data?.message) ? quoteRes.data.message : "");
                setLoading(false);
                return;
            }

            setError(quoteRes?.data?.message || "Something went wrong.");
            setLoading(false);
        } catch (error) {
            setLoading(false);
            const serverErrors = error?.response?.data?.errors;
            const applied = applyServerErrors(serverErrors);
            const message = error?.response?.data?.message || "Something went wrong. Please try again.";
            setError(applied ? "" : message);
        }
    };

    return (
        <>
            {completed ? (
                <div className="flex flex-col items-center justify-center gap-6 h-full w-full p-4">
                    <h2 className="uppercase text-2xl text-center text-gray-700">
                        {customerStripeEnabled ? ( <> Successfully <br /> added to basket </>) : ( <> Quote requested <br /> successfully </> )}
                    </h2>
                    {customerStripeEnabled && basket && <QuotePriceSummary basket={basket} />}
                    <button
                        onClick={() => {
                            if (customerStripeEnabled) {
                                trackEvent("begin_checkout", basketEcommerceParams(basket, "retail"));
                                setCurrentPage("basket");
                            } else {
                                setCurrentPage("home");
                            }
                        }}
                        type="button"
                        className={buttonVariants({
                            className:
                                "flex items-center justify-between text-center gap-1 w-fit uppercase",
                        })}
                    >
                        {customerStripeEnabled ? "go to checkout" : "back to configurator"}
                    </button>
                </div>
            ) : (
                <form
                    onSubmit={methods.handleSubmit(onSubmit)}
                    className="flex flex-col items-start justify-between w-full h-full min-h-0 px-4 py-4"
                >
                    <div className="flex flex-col gap-4 items-center w-full [&>div]:w-full flex-1 min-h-0 overflow-y-auto pb-4 px-2 mx-2">

                        {customerStripeEnabled && (
                            <PriceAccessNote />
                        )}

                        {/* Trade Partner Benefits Card */}
                        <div className="w-full rounded-lg border-2 border-primary/30 bg-gradient-to-b from-primary/5 to-white">
                            <button
                                type="button"
                                onClick={() => setTradeInfoExpanded(!tradeInfoExpanded)}
                                className="flex items-center justify-between w-full px-4 py-3 text-left"
                            >
                                <div className="flex items-center gap-2">
                                    <FontAwesomeIcon icon={faUserPlus} className="text-primary" />
                                    <span className="text-sm font-semibold text-gray-700 uppercase">
                                        Are you a trade professional?
                                    </span>
                                </div>
                                <FontAwesomeIcon
                                    icon={tradeInfoExpanded ? faChevronUp : faChevronDown}
                                    className="text-gray-400 text-xs"
                                />
                            </button>

                            {tradeInfoExpanded && (
                                <div className="px-4 pb-3 space-y-2">
                                    <p className="text-xs text-gray-500">
                                        Sign up as a trade partner and enjoy exclusive benefits:
                                    </p>
                                    <ul className="space-y-1.5">
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon={faCheck} className="text-green-500 text-xs mt-0.5 flex-shrink-0" />
                                            <span className="text-xs text-gray-600">
                                                <strong className="text-gray-700">Discounted trade pricing</strong> — significant savings on every door
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon={faCheck} className="text-green-500 text-xs mt-0.5 flex-shrink-0" />
                                            <span className="text-xs text-gray-600">
                                                <strong className="text-gray-700">Dedicated trade account</strong> — with order history &amp; streamlined checkout
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon={faCheck} className="text-green-500 text-xs mt-0.5 flex-shrink-0" />
                                            <span className="text-xs text-gray-600">
                                                <strong className="text-gray-700">Priority support</strong> — dedicated account management &amp; technical assistance
                                            </span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <FontAwesomeIcon icon={faCheck} className="text-green-500 text-xs mt-0.5 flex-shrink-0" />
                                            <span className="text-xs text-gray-600">
                                                <strong className="text-gray-700">Business invoicing</strong> — simplified invoicing for VAT-registered businesses
                                            </span>
                                        </li>
                                    </ul>

                                    <p className="text-[10px] text-gray-400 italic">
                                        Apply online — we'll verify, approve and provide your trade login.
                                    </p>

                                    <a
                                        href="https://rhinodoor.co.uk/become-a-trade-distributor/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-2 w-full rounded-md border-2 border-primary bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-primary/90 transition-colors uppercase"
                                    >
                                        <FontAwesomeIcon icon={faUserPlus} />
                                        Apply for Trade Account
                                    </a>
                                </div>
                            )}
                        </div>

                        <FormField
                            control={control}
                            name="first_name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        First Name
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your First Name"
                                            {...field}
                                        />
                                    </FormControl>
                                    {methods.formState.errors.first_name && (
                                        <FormMessage>
                                            {methods.formState.errors.first_name.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="last_name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        Last Name
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your Last Name"
                                            {...field}
                                        />
                                    </FormControl>
                                    {methods.formState.errors.last_name && (
                                        <FormMessage>
                                            {methods.formState.errors.last_name.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        Email
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your Email"
                                            {...field}
                                        />
                                    </FormControl>
                                    {methods.formState.errors.email && (
                                        <FormMessage>
                                            {methods.formState.errors.email.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="postcode"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        Postcode
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your Postcode"
                                            {...field}
                                        />
                                    </FormControl>
                                    {methods.formState.errors.postcode && (
                                        <FormMessage>
                                            {methods.formState.errors.postcode.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={control}
                            name="phone"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-gray-700">
                                        Phone Number
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="text"
                                            icon
                                            className="text-gray-700"
                                            placeholder="Enter Your Phone Number"
                                            {...field}
                                        />
                                    </FormControl>
                                    {methods.formState.errors.phone && (
                                        <FormMessage>
                                            {methods.formState.errors.phone.message}
                                        </FormMessage>
                                    )}
                                </FormItem>
                            )}
                        />
                        {error && (
                            <div className="text-red-500 text-sm">{error}</div>
                        )}
                    </div>

                    <div className="flex flex-col gap-3 w-full flex-shrink-0">
                        <Button type="submit" disabled={loading} className="w-full">
                            {customerStripeEnabled ? "Get quote" : "Request Quote"}
                        </Button>
                    </div>
                </form>
            )}
        </>
    );
};

export default QuoteForm;
